import React, { Component, Fragment, useState } from 'react';
import { compose } from '../../reactApolloCompat';
import { withRouter } from '../../util/withRouter';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import withStyles from '@mui/styles/withStyles';
import { InlineFieldRightAlignChildren } from '../../component/form/Inline';
import Typography from '../../component/Typography';
import Grid from '../../component/form/Grid';
import Paper from '@mui/material/Paper';
import Label from '../../component/form/Label';
import GatedComponent, { permissionsQuery } from '../../component/GatedComponent';
import TextField from '../../component/form/TextField';
import TagButton from '../../component/form/TagButton';
import { SaveButton } from '../../component/form/PrimaryButton';
import InputAdornment from '@mui/material/InputAdornment';
import Icon from '@mui/material/Icon';
import SaveIcon from '../../component/icon/SaveIcon';
import Hidden from '../../component/Hidden';
import { getAuthClient, getClient, getMyClient } from '../../apollo';
import gql from 'graphql-tag';
import Spinner from '../../component/Spinner';
import ColumnLayout from '../../component/ColumnLayout';
import { getAllOffices } from '../../util/brands';
import MenuItem from '@mui/material/MenuItem';
import ErrorFallback from '../../component/ErrorFallback';
import { withErrorBoundary } from '@sentry/react';

const UpdatePhoneMutation = gql`
    mutation UpdatePhoneMutation($input: UpdateMemberInput!) {
        updateMember(input: $input) {
            ID
        }
    }
`;

const updateMemberLastOfficeIDMutation = gql`
    mutation UpdateMemberLastOfficeID($memberid: ID!, $lastofficeid: ID!) {
        updateMemberLastOfficeID(ID: $memberid, LastOfficeID: $lastofficeid) {
            ID
        }
    }
`;

function RenderUser({ user }) {
    const [lastOfficeID, setLastOfficeID] = useState(user.MyLastOfficeID);
    const [Phone, setPhone] = useState(user.Phone);
    const [busy, setBusy] = useState(false);
    const savePhone = async () => {
        const input = { ID: user.ID, Phone };
        const asyncQuery = await getMyClient().mutate({ mutation: UpdatePhoneMutation, variables: { input } });
        return asyncQuery && !!asyncQuery.data;
    };
    const saveOffice = async () => {
        const input = { memberid: Number(user.ID), lastofficeid: lastOfficeID };
        const asyncQuery = await getClient().mutate({
            mutation: updateMemberLastOfficeIDMutation,
            variables: { ...input }
        });
        return asyncQuery && !!asyncQuery.data;
    };
    const isPhoneDirty = Phone !== user.Phone;
    const isOfficeDirty = lastOfficeID !== user.MyLastOfficeID;
    const isPhoneBusy = busy && Phone !== user.Phone;
    const isOfficeBusy = busy && lastOfficeID !== user.MyLastOfficeID;
    return (
        <ColumnLayout>
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <TextField label={'First Name'} value={user.FirstName} readOnly />
                </Grid>
                <Grid item xs={6}>
                    <TextField label={'Surname'} value={user.Surname} readOnly />
                </Grid>
                <Grid bucket>
                    <Grid item xs={12}>
                        <Label text={'My Contact Details'} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label={'Email'} value={user.Email} readOnly />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={'Phone'}
                            value={Phone}
                            disabled={isPhoneBusy}
                            onChange={v => setPhone(v.target.value)}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon color="primary">phone</Icon>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <SaveButton
                            disabled={isPhoneBusy || !isPhoneDirty}
                            onClick={() => {
                                setBusy(true);
                                savePhone().then(() => {
                                    getAuthClient().writeQuery({
                                        query: permissionsQuery,
                                        data: {
                                            readCurrentUser: { ...user, Phone: Phone }
                                        }
                                    });
                                    setBusy(false);
                                });
                            }}
                        >
                            {isPhoneBusy ? <Spinner /> : <SaveIcon />}
                            {isPhoneBusy ? <Hidden mdDown> Updating...</Hidden> : <Hidden mdDown> Update Phone</Hidden>}
                        </SaveButton>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Label text={'My Teams'} />
                </Grid>
                <Grid item xs={12}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {user.Groups && user.Groups.map(e => <TagButton key={e.ID} text={e.Title} />)}
                    </div>
                </Grid>
                <Grid bucket>
                    <Grid item xs={12}>
                        <Label text={'My Current Office'} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            disabled={isOfficeBusy}
                            onChange={e => setLastOfficeID(e.target.value)}
                            label="Office"
                            value={lastOfficeID}
                            select
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon color="primary">business</Icon>
                                    </InputAdornment>
                                )
                            }}
                        >
                            {getAllOffices().map(option => (
                                <MenuItem key={option.ID} value={option.ID}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <SaveButton
                            disabled={busy || !isOfficeDirty}
                            onClick={() => {
                                setBusy(true);
                                saveOffice().then(() => {
                                    setBusy(false);
                                    window.location.reload();
                                });
                            }}
                        >
                            {isOfficeBusy ? <Spinner /> : <SaveIcon />}
                            {isOfficeBusy ? (
                                <Hidden mdDown> Updating...</Hidden>
                            ) : (
                                <Hidden mdDown> Update Office</Hidden>
                            )}
                        </SaveButton>
                    </Grid>
                </Grid>
            </Grid>
        </ColumnLayout>
    );
}

class Account extends Component {
    render() {
        const { classes } = this.props;
        return (
            <Fragment>
                <Typography variant="display4" gutterBottom className={classes.pageTitle}>
                    <InlineFieldRightAlignChildren lineHeight={'normal'}>
                        <span>Account Settings</span>
                    </InlineFieldRightAlignChildren>
                </Typography>
                <Paper className={classes.paper} elevation={0}>
                    <GatedComponent showComponentCode={'OBJ_EDIT_ALL'}>
                        {(canEdit, data) => <RenderUser user={data.readCurrentUser} />}
                    </GatedComponent>
                </Paper>
            </Fragment>
        );
    }
}

const styles = ({ spacing, palette, funeralHome }) => ({
    paper: {
        padding: spacing(3)
    },
    pageTitle: {
        color: palette.contentForeground[funeralHome]
    }
});

export default withErrorBoundary(compose(withRouter, withSnackbarMessage, withStyles(styles))(Account), {
    fallback: ErrorFallback
});
